import { createFeature, createReducer, on } from "@ngrx/store";
import {
    FormMutationState,
    FormSection,
    FormSubsection,
    ManagedForm,
} from "../models/form.models";
import { FormsActions } from "./forms.actions";

export interface FormsState {
    forms: ManagedForm[];
    subsections: FormSubsection[];
    selectedFormId: string | null;
    formQuery: string;
    questionQuery: string;
    section: FormSection;
    expandedSubsectionIds: string[];
    loading: boolean;
    loaded: boolean;
    error: boolean;
    mutation: FormMutationState | null;
}

const initialState: FormsState = {
    forms: [],
    subsections: [],
    selectedFormId: null,
    formQuery: "",
    questionQuery: "",
    section: "questions",
    expandedSubsectionIds: [],
    loading: false,
    loaded: false,
    error: false,
    mutation: null,
};

const sortForms = (forms: ManagedForm[]) =>
    [...forms].sort((a, b) => a.title.localeCompare(b.title));

export const formsFeature = createFeature({
    name: "forms",
    reducer: createReducer(
        initialState,
        on(
            FormsActions.pageOpened,
            (state): FormsState => ({
                ...state,
                loading: true,
                error: false,
            })
        ),
        on(
            FormsActions.dataLoaded,
            (state, { forms, subsections }): FormsState => ({
                ...state,
                forms,
                subsections,
                selectedFormId: forms.some(
                    (form) => form.gmvid === state.selectedFormId
                )
                    ? state.selectedFormId
                    : (forms[0]?.gmvid ?? null),
                loading: false,
                loaded: true,
                error: false,
            })
        ),
        on(
            FormsActions.dataLoadFailed,
            (state): FormsState => ({
                ...state,
                loading: false,
                loaded: true,
                error: true,
            })
        ),
        on(
            FormsActions.formSelected,
            (state, { formId }): FormsState => ({
                ...state,
                selectedFormId: formId,
                questionQuery: "",
                expandedSubsectionIds: [],
            })
        ),
        on(
            FormsActions.formQueryChanged,
            (state, { query }): FormsState => ({ ...state, formQuery: query })
        ),
        on(
            FormsActions.questionQueryChanged,
            (state, { query }): FormsState => ({
                ...state,
                questionQuery: query,
            })
        ),
        on(
            FormsActions.sectionChanged,
            (state, { section }): FormsState => ({
                ...state,
                section,
                questionQuery: "",
            })
        ),
        on(
            FormsActions.subsectionToggled,
            (state, { subsectionId }): FormsState => ({
                ...state,
                expandedSubsectionIds: state.expandedSubsectionIds.includes(
                    subsectionId
                )
                    ? state.expandedSubsectionIds.filter(
                          (id) => id !== subsectionId
                      )
                    : [...state.expandedSubsectionIds, subsectionId],
            })
        ),
        on(
            FormsActions.createRequested,
            (state): FormsState => pending(state, "create")
        ),
        on(
            FormsActions.createSucceeded,
            (state, { form }): FormsState => ({
                ...state,
                forms: sortForms([...state.forms, form]),
                selectedFormId: form.gmvid,
                section: "questions",
                mutation: { type: "create", status: "success" },
            })
        ),
        on(
            FormsActions.duplicateRequested,
            (state): FormsState => pending(state, "duplicate")
        ),
        on(
            FormsActions.duplicateSucceeded,
            (state, { form }): FormsState => ({
                ...state,
                forms: sortForms([...state.forms, form]),
                selectedFormId: form.gmvid,
                mutation: { type: "duplicate", status: "success" },
            })
        ),
        on(
            FormsActions.updateFormRequested,
            (state, { mutationType }): FormsState =>
                pending(state, mutationType)
        ),
        on(
            FormsActions.updateFormSucceeded,
            (state, { form, mutationType }): FormsState => ({
                ...state,
                forms: sortForms(
                    state.forms.map((item) =>
                        item.gmvid === form.gmvid ? form : item
                    )
                ),
                mutation: { type: mutationType, status: "success" },
            })
        ),
        on(
            FormsActions.deleteRequested,
            (state): FormsState => pending(state, "delete")
        ),
        on(FormsActions.deleteSucceeded, (state, { formId }): FormsState => {
            const forms = state.forms.filter((form) => form.gmvid !== formId);
            return {
                ...state,
                forms,
                selectedFormId: forms[0]?.gmvid ?? null,
                mutation: { type: "delete", status: "success" },
            };
        }),
        on(
            FormsActions.createSubsectionRequested,
            (state): FormsState => pending(state, "create-subsection")
        ),
        on(
            FormsActions.createSubsectionSucceeded,
            (state, { form, subsection }): FormsState => ({
                ...state,
                forms: sortForms(
                    state.forms.map((item) =>
                        item.gmvid === form.gmvid ? form : item
                    )
                ),
                subsections: [...state.subsections, subsection],
                expandedSubsectionIds: [
                    ...state.expandedSubsectionIds,
                    subsection.gmvid,
                ],
                mutation: { type: "create-subsection", status: "success" },
            })
        ),
        on(
            FormsActions.updateSubsectionRequested,
            (state): FormsState => pending(state, "update-subsection")
        ),
        on(
            FormsActions.updateSubsectionSucceeded,
            (state, { subsection, mutationType }): FormsState => ({
                ...state,
                subsections: state.subsections.map((item) =>
                    item.gmvid === subsection.gmvid ? subsection : item
                ),
                mutation: { type: mutationType, status: "success" },
            })
        ),
        on(
            FormsActions.deleteSubsectionRequested,
            (state): FormsState => pending(state, "delete-subsection")
        ),
        on(
            FormsActions.deleteSubsectionSucceeded,
            (state, { form, subsectionId }): FormsState => ({
                ...state,
                forms: sortForms(
                    state.forms.map((item) =>
                        item.gmvid === form.gmvid ? form : item
                    )
                ),
                subsections: state.subsections.filter(
                    (item) => item.gmvid !== subsectionId
                ),
                expandedSubsectionIds: state.expandedSubsectionIds.filter(
                    (id) => id !== subsectionId
                ),
                mutation: { type: "delete-subsection", status: "success" },
            })
        ),
        on(
            FormsActions.reorderSubsectionsRequested,
            (state): FormsState => pending(state, "reorder-subsections")
        ),
        on(
            FormsActions.questionSaveRequested,
            (state, { questionIndex }): FormsState =>
                pending(
                    state,
                    questionIndex === null
                        ? "create-question"
                        : "update-question"
                )
        ),
        on(
            FormsActions.deleteQuestionRequested,
            (state): FormsState => pending(state, "delete-question")
        ),
        on(
            FormsActions.reorderQuestionsRequested,
            (state): FormsState => pending(state, "reorder-questions")
        ),
        on(
            FormsActions.signaturesSaveRequested,
            (state): FormsState => pending(state, "update-signatures")
        ),
        on(
            FormsActions.mutationFailed,
            (state): FormsState => ({
                ...state,
                mutation: state.mutation
                    ? { ...state.mutation, status: "error" }
                    : null,
            })
        ),
        on(
            FormsActions.mutationStatusCleared,
            (state): FormsState => ({ ...state, mutation: null })
        )
    ),
});

function pending(
    state: FormsState,
    type: FormMutationState["type"]
): FormsState {
    return { ...state, mutation: { type, status: "pending" } };
}
